import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api.js'
import Kopfzeile from '../Kopfzeile.jsx'
import Dialog from '../Dialog.jsx'
import Icon from '../Icon.jsx'
import { MONATSNAMEN, STATUSTEXT, alsDauer, euro, zeitpunkt } from '../format.js'

/**
 * Der Sammelblick der Lohnstelle auf einen Monat.
 *
 * Zeigt alle Mitarbeiter mit Stand und Summe, hebt hervor, was freigegeben
 * und noch nicht abgerechnet ist, und bietet Export und Vermerk.
 */
export default function Abrechnung() {
  const [parameter, setParameter] = useSearchParams()
  const jetzt = new Date()
  const jahr = Number(parameter.get('jahr')) || jetzt.getFullYear()
  const monat = Number(parameter.get('monat')) || jetzt.getMonth() + 1

  const [daten, setDaten] = useState(null)
  const [fehler, setFehler] = useState('')
  const [meldung, setMeldung] = useState('')
  const [fragt, setFragt] = useState(false)

  const laden = useCallback(() => {
    setFehler('')
    api(`/abrechnung/${jahr}/${monat}`)
      .then(setDaten)
      .catch((e) => setFehler(e.message))
  }, [jahr, monat])

  useEffect(laden, [laden])

  function verschiebe(schritte) {
    const roh = monat - 1 + schritte
    setParameter({
      jahr: String(jahr + Math.floor(roh / 12)),
      monat: String((((roh % 12) + 12) % 12) + 1),
    })
    setMeldung('')
  }

  const anstehend = daten?.zurAbrechnung.length ?? 0

  return (
    <main className="huelle">
      <Kopfzeile />

      <div className="monatsleiste">
        <button
          className="knopf knopf--geist knopf--symbol"
          type="button"
          onClick={() => verschiebe(-1)}
          title="Vorheriger Monat"
        >
          <Icon name="links" />
          <span className="nur-vorlesen">Vorheriger Monat</span>
        </button>
        <h2 className="monatstitel">
          Abrechnung {MONATSNAMEN[monat - 1]} {jahr}
        </h2>
        <button
          className="knopf knopf--geist knopf--symbol"
          type="button"
          onClick={() => verschiebe(1)}
          title="Nächster Monat"
        >
          <Icon name="rechts" />
          <span className="nur-vorlesen">Nächster Monat</span>
        </button>
      </div>

      {meldung && (
        <div className="meldung ok" role="status">
          <Icon name="haken" groesse={18} />
          <span>{meldung}</span>
        </div>
      )}
      {fehler && (
        <div className="meldung fehler" role="alert">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}
      {!daten && !fehler && <p className="hinweis">Einen Moment …</p>}

      {daten && (
        <>
          <section className="karte summenkarte">
            <div className="summenzeile summenzeile--gross">
              <span>Zur Abrechnung</span>
              <strong>{alsDauer(daten.summeMinuten)}</strong>
            </div>
            <div className="summenzeile">
              <span className="gedaempft">Freigegeben und offen</span>
              <span className="gedaempft">
                {anstehend} {anstehend === 1 ? 'Person' : 'Personen'}
              </span>
            </div>
            {daten.summeVerdienstCent > 0 && (
              <div className="summenzeile">
                <span>Lohnsumme (Minijob)</span>
                <strong>{euro(daten.summeVerdienstCent)}</strong>
              </div>
            )}
          </section>

          <section className="karte karte--liste">
            <h2>Monatsblätter</h2>
            {daten.zeilen.length === 0 && (
              <div className="leerzustand">
                <Icon name="leute" groesse={32} strich={1.5} />
                <p>Keine aktiven Mitarbeiter.</p>
              </div>
            )}
            {daten.zeilen.map((z) => (
              <Link
                key={z.benutzerId}
                className="uebersichtszeile"
                to={`/?jahr=${jahr}&monat=${monat}&benutzer=${z.benutzerId}`}
              >
                <div className="uebersichtszeile__name">
                  <strong>{z.name}</strong>
                  <span className="konto__marker">
                    <span className={`abzeichen abzeichen--${z.status}`}>
                      {STATUSTEXT[z.status]}
                    </span>
                    {z.abgerechnetAm && (
                      <span className="abzeichen abzeichen--genehmigt">
                        <Icon name="haken" groesse={12} strich={2.5} /> abgerechnet
                      </span>
                    )}
                  </span>
                </div>
                <div className="uebersichtszeile__zahlen">
                  <div className="uebersichtszeile__werte">
                    <span>{alsDauer(z.summeMinuten)}</span>
                    {z.verdienstCent !== null && (
                      <small className={z.ueberGrenze ? 'fehler' : 'gedaempft'}>
                        {euro(z.verdienstCent)}
                      </small>
                    )}
                  </div>
                  <Icon name="rechts" groesse={18} klasse="gedaempft" />
                </div>
              </Link>
            ))}
          </section>

          <section className="karte">
            <h2>Lohnbuchhaltung</h2>
            <p className="hinweis hinweis--oben">
              {anstehend === 0
                ? 'Derzeit ist nichts freigegeben, was noch nicht abgerechnet wäre.'
                : `${anstehend} freigegebene ${anstehend === 1 ? 'Person wartet' : 'Personen warten'} auf die Abrechnung.`}
            </p>
            <div className="knopfreihe">
              <a
                className="knopf knopf--leise"
                href={`/api/abrechnung/${jahr}/${monat}.csv`}
              >
                <Icon name="tabelle" /> Freigegebene als CSV
              </a>
              <a
                className="knopf knopf--geist"
                href={`/api/abrechnung/${jahr}/${monat}.csv?alle=1`}
              >
                <Icon name="tabelle" /> Alle Stände
              </a>
            </div>
            <div className="knopfreihe" style={{ marginTop: '.75rem' }}>
              <button
                className="knopf knopf--haupt"
                type="button"
                disabled={anstehend === 0}
                onClick={() => setFragt(true)}
              >
                <Icon name="haken" /> Als abgerechnet vermerken
              </button>
            </div>
          </section>
        </>
      )}

      {fragt && (
        <Abrechnungsdialog
          jahr={jahr}
          monat={monat}
          daten={daten}
          beiSchliessen={() => setFragt(false)}
          beiFertig={(text) => {
            setMeldung(text)
            setFragt(false)
            laden()
          }}
        />
      )}
    </main>
  )
}

function Abrechnungsdialog({ jahr, monat, daten, beiSchliessen, beiFertig }) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')

  const betroffen = daten.zeilen.filter((z) => daten.zurAbrechnung.includes(z.benutzerId))

  async function vermerken() {
    setLaeuft(true)
    setFehler('')
    try {
      const antwort = await api(`/abrechnung/${jahr}/${monat}`, { methode: 'POST', koerper: {} })
      beiFertig(
        `${antwort.abgerechnet} ${antwort.abgerechnet === 1 ? 'Monatsblatt' : 'Monatsblätter'} als abgerechnet vermerkt.`
      )
    } catch (e) {
      setFehler(e.message)
      setLaeuft(false)
    }
  }

  return (
    <Dialog
      titel="Als abgerechnet vermerken"
      symbol="haken"
      untertitel={`${MONATSNAMEN[monat - 1]} ${jahr}`}
      beiSchliessen={() => !laeuft && beiSchliessen()}
    >
      <p className="hinweis hinweis--oben">
        Der Vermerk hält fest, dass diese Monate an die Lohnbuchhaltung gegangen sind. Danach
        lässt sich die Freigabe nicht mehr widerrufen.
      </p>

      <div className="summenkarte">
        {betroffen.map((z) => (
          <div className="summenzeile" key={z.benutzerId}>
            <span>{z.name}</span>
            <strong>
              {alsDauer(z.summeMinuten)}
              {z.verdienstCent !== null && (
                <span className="gedaempft"> · {euro(z.verdienstCent)}</span>
              )}
            </strong>
          </div>
        ))}
      </div>

      {fehler && (
        <div className="meldung fehler" role="alert" style={{ marginTop: '1rem' }}>
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      <div className="dialog__fuss">
        <button className="knopf knopf--leise" type="button" onClick={beiSchliessen} disabled={laeuft}>
          Abbrechen
        </button>
        <button className="knopf knopf--haupt" type="button" onClick={vermerken} disabled={laeuft}>
          <Icon name="haken" /> {laeuft ? 'Wird vermerkt …' : `${betroffen.length} vermerken`}
        </button>
      </div>
    </Dialog>
  )
}
